import { ProductService } from "~/features/addToCart/api/ProductService";
import { useMemo } from "react";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import {
  type AllScreenSizes,
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";
import { PreferredProductTile } from "~/features/productDetails/components/PhaseInOut/PreferredProducts/components/PreferredProductTile/PreferredProductTile";
import { useProductContext } from "~/domains/product/ProductContext";
import { Link } from "~/common/components/Link";
import { PREFERRED_PRODUCTS_ID } from "~/features/productDetails/constants";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type PreferredProductCodesContext } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { useTranslation } from "react-i18next";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Box, Stack, Typography, styled } from "~/common/mui";

const PreferredProductsRoot = styled(Box)(({ theme }) => ({
  background: theme.palette.surface.main,
  display: "flex",
  flexFlow: "column",
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  gap: theme.spacing(3),
  [theme.breakpoints.up("desktop-l")]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const PreferredProductsTitle = styled(Typography)(({ theme }) => ({
  margin: 0,
}));

const TilesGrid = styled("div")(({ theme }) => ({
  "--column-amount": 2,
  display: "grid",
  gridTemplateColumns: "repeat(var(--column-amount), minmax(0, 1fr))",
  gap: theme.spacing(2),
  position: "relative",
  zIndex: 2,
  [theme.breakpoints.up("desktop-l")]: {
    "--column-amount": 3,
  },
}));

interface WrapperProps {
  className?: string;
  data: PreferredProductCodesContext;
}

export const PreferredProducts = ({ data, ...rest }: WrapperProps) => {
  const productsMap = ProductService.useMultipleProductDescriptors({
    productCodes: data.codes,
    priceReference: getInitialPriceReference(),
  });
  const mainProduct = useProductContext();
  const products = useMemo(() => {
    return data.codes.map((code) => productsMap[code]);
  }, [productsMap, data.codes]);

  if (data.status === "loading") {
    return (
      <PreferredProductsRoot className={rest.className}>
        <CenterContents>
          <CircleSpinner />
        </CenterContents>
      </PreferredProductsRoot>
    );
  }

  if (data.status === "loaded") {
    return (
      <PreferredProductsInner
        {...rest}
        preferredProducts={products}
        mainProduct={mainProduct}
        totalPreferredAmount={data.totalAmount}
      />
    );
  }

  return null;
};

interface Props {
  className?: string;
  preferredProducts: ProductDescriptor[];
  mainProduct: ProductData;
  totalPreferredAmount: number;
}

export const PreferredProductsInner = (props: Props) => {
  const { t } = useTranslation();
  const isMinDesktop = useIsMinDesktopSize();
  const showMoreThreshold = isMinDesktop ? 3 : 2;
  const showMore = props.totalPreferredAmount > showMoreThreshold;

  const screenSize = useNumericScreenSize();
  const tileAmount = TILE_AMOUNTS[screenSize];

  const designation = props.mainProduct.designation;

  return (
    <PreferredProductsRoot
      className={props.className}
      tracking-aux-ppr-parent-product-name={designation}
      tracking-aux-ppr-parent-product-id={props.mainProduct.code}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <PreferredProductsTitle variant="titleMedium">{t("pdp.preferredProducts.title", { code: designation })}</PreferredProductsTitle>
        {showMore && (
          <Link tracking-aux-ppr-show-more="true" href={"#" + PREFERRED_PRODUCTS_ID}>
            {t("page.pdp.phaseinout.preferred.showmore")}
          </Link>
        )}
      </Stack>
      <TilesGrid>
        {props.preferredProducts.slice(0, tileAmount).map((product) => (
          <PreferredProductTile key={product.base.code} product={product} />
        ))}
      </TilesGrid>
    </PreferredProductsRoot>
  );
};

const TILE_AMOUNTS: Record<AllScreenSizes, number> = {
  [SIZE_BELOW_PHONE]: 1,
  [SIZE_PHONE]: 1,
  [SIZE_TABLET]: 1,
  [SIZE_DESKTOP_S]: 2,
  [SIZE_DESKTOP_M]: 2,
  [SIZE_DESKTOP_L]: 3,
};
